import { useEffect, useState } from "react";
import { Loader2, ArrowLeft, ArrowRight, FileStack } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getPdfDocument } from "@/lib/pdfjs";

interface PdfMergeOrderDialogProps {
  files: File[];
  open: boolean;
  onClose: () => void;
  onConfirm: (orderedFiles: File[]) => void;
}

interface Item {
  file: File;
  thumb: string | null;
}

const renderThumb = async (file: File): Promise<string | null> => {
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
  try {
    if (isPdf) {
      const buf = await file.arrayBuffer();
      const pdf = await (await getPdfDocument({ data: buf })).promise;
      const page = await pdf.getPage(1);
      const vp = page.getViewport({ scale: 0.5 });
      const canvas = document.createElement("canvas");
      canvas.width = vp.width;
      canvas.height = vp.height;
      await page.render({ canvas, canvasContext: canvas.getContext("2d")!, viewport: vp }).promise;
      const url = canvas.toDataURL("image/jpeg", 0.7);
      canvas.width = 0;
      canvas.height = 0;
      return url;
    }
    return URL.createObjectURL(file);
  } catch (e) {
    console.error("No se pudo generar la miniatura:", e);
    return null;
  }
};

const PdfMergeOrderDialog = ({ files, open, onClose, onConfirm }: PdfMergeOrderDialogProps) => {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || files.length === 0) {
      setItems([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setItems(files.map((file) => ({ file, thumb: null })));
    (async () => {
      const thumbs = await Promise.all(files.map((f) => renderThumb(f)));
      if (cancelled) return;
      setItems(files.map((file, i) => ({ file, thumb: thumbs[i] })));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, files]);

  const move = (index: number, delta: number) => {
    setItems((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[90vw] h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileStack className="h-4 w-4" /> Ordenar documentos antes de unir
          </DialogTitle>
          <DialogDescription className="text-xs">
            Revisa la vista previa y coloca los documentos en el orden en el que quieres que aparezcan
            en el PDF unido.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto border rounded-md p-4 bg-muted/20">
          {loading && items.every((i) => !i.thumb) ? (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Cargando vistas previas…
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {items.map((item, idx) => (
                <div
                  key={`${item.file.name}-${idx}`}
                  className="w-44 border rounded-md bg-background p-2 flex flex-col gap-2"
                >
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {idx + 1}
                  </div>
                  <div className="flex items-center justify-center h-44 overflow-hidden rounded bg-muted/40">
                    {item.thumb ? (
                      <img src={item.thumb} alt={item.file.name} className="max-h-44 object-contain" />
                    ) : (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  <div className="text-[11px] truncate" title={item.file.name}>
                    {item.file.name}
                  </div>
                  <div className="flex justify-between">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 px-2"
                      disabled={idx === 0}
                      onClick={() => move(idx, -1)}
                      aria-label="Mover antes"
                    >
                      <ArrowLeft className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 px-2"
                      disabled={idx === items.length - 1}
                      onClick={() => move(idx, 1)}
                      aria-label="Mover después"
                    >
                      <ArrowRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <span className="text-xs text-muted-foreground mr-auto self-center">
            {items.length} documentos en este orden
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={items.length < 2}
            onClick={() => onConfirm(items.map((i) => i.file))}
            className="gap-1"
          >
            <FileStack className="h-3 w-3" /> Unir en este orden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PdfMergeOrderDialog;
